import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import DescriptionCell from '../components/DescriptionCell';
import SubscribeModal from '../components/SubscribeModal';
import './Home.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const ROOT_MENU_ID = 1;
const HOME_CATEGORY_MENU_ID = 82;

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

const formatPrice = (price) =>
  price != null ? `₹${parseFloat(price).toFixed(2)}` : null;

const formatCutoffTime = (timeStr) =>
  timeStr ? timeStr.slice(0, 5) : null;

const isPastCutoff = (cutoffTimeStr, orderAfterCutoff) => {
  if (!cutoffTimeStr || orderAfterCutoff === 'Y') return false;
  const [hours, minutes] = cutoffTimeStr.split(':').map(Number);
  const cutoff = new Date();
  cutoff.setHours(hours, minutes, 0, 0);
  return new Date() > cutoff;
};

// Returns true when cutoff has passed AND ordering after cutoff is permitted
const isAfterCutoffAllowed = (cutoffTimeStr, orderAfterCutoff) => {
  if (!cutoffTimeStr || orderAfterCutoff !== 'Y') return false;
  const [hours, minutes] = cutoffTimeStr.split(':').map(Number);
  const cutoff = new Date();
  cutoff.setHours(hours, minutes, 0, 0);
  return new Date() > cutoff;
};

// Returns { sat, sun } for the next Saturday on-or-after baseDate, plus the following Sunday
const nextWeekend = (baseDate) => {
  const d = new Date(baseDate);
  const dow = d.getDay();
  const daysToSat = (6 - dow + 7) % 7;
  const daysToSun = (7 - dow) % 7;
  const satDate = new Date(d);
  satDate.setDate(d.getDate() + daysToSat);
  const sunDate = new Date(d);
  sunDate.setDate(d.getDate() + daysToSun);
  // return in chronological order
  return satDate <= sunDate
    ? { sat: satDate, sun: sunDate }
    : { sat: sunDate, sun: satDate };
};

const fmtDate = (d) => d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

const computeDeliveryLabel = (product) => {
  const lead = parseInt(product.order_lead_days, 10) || 0;
  const afterCutoff = isAfterCutoffAllowed(product.order_cutoff_time, product.order_after_cutoff);

  if (product.days_available === 'WE') {
    // Weekend products: find next Sat+Sun on or after (today + lead + 1 if past cutoff)
    const base = new Date();
    base.setDate(base.getDate() + lead + (afterCutoff ? 1 : 0));
    const { sat, sun } = nextWeekend(base);
    return `${fmtDate(sat)} & ${fmtDate(sun)}`;
  }

  // AD (all days): existing logic — only label when past cutoff and order_after_cutoff='Y'
  if (!afterCutoff) return null;
  const date = new Date();
  date.setDate(date.getDate() + lead + 1);
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};


const Home = () => {
  const { user } = useContext(AuthContext);
  const { addToCart, removeFromCart, getQuantity } = useCart();
  const navigate = useNavigate();

  const isCS        = user?.user_type === 'CS';
  const isStoreOrCS = user?.user_type === 'S' || isCS;
  const isLive      = isCS || (user?.user_type === 'S' && (localStorage.getItem('checkoutMode') || 'live') === 'live');

  const [searchText, setSearchText] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [rootBannerText, setRootBannerText] = useState(null);
  const [subscribingProduct, setSubscribingProduct] = useState(null);
  const searchInputRef = useRef(null);

  const [homeCategories, setHomeCategories] = useState([]);
  const [homeCatLoading, setHomeCatLoading] = useState(true);

  // Shrink search bar when user scrolls down inside main-content
  useEffect(() => {
    const scrollTarget = document.querySelector('.main-content') || window;
    const handleScroll = () => {
      const top = scrollTarget === window ? window.scrollY : scrollTarget.scrollTop;
      setScrolled(top > 60);
    };
    scrollTarget.addEventListener('scroll', handleScroll);
    return () => scrollTarget.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch root menu pages on mount to get the header banner text
  useEffect(() => {
    axios.get(`${API_URL}/menuRoutes/menu-pages`, {
      params: { menu_id: ROOT_MENU_ID },
      headers: { Authorization: `Bearer ${user?.token}` },
    }).then((res) => {
      const header = res.data.find(
        (p) => p.page_type === 'Header' && p.display_text
      );
      setRootBannerText(header?.display_text || null);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch children of HOME_CATEGORY_MENU_ID on mount for the default category grid
  useEffect(() => {
    setHomeCatLoading(true);
    axios.get(`${API_URL}/menuRoutes/children`, {
      params: { parent_id: HOME_CATEGORY_MENU_ID },
      headers: { Authorization: `Bearer ${user?.token}` },
    }).then((res) => {
      setHomeCategories(res.data);
    }).catch(() => {
      setHomeCategories([]);
    }).finally(() => {
      setHomeCatLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced API call whenever searchText changes.
  // Store partners always show all products (fetch on empty too); others only fetch when text is entered.
  useEffect(() => {
    if (!searchText.trim()) {
      if (isStoreOrCS) {
        fetchProducts('');
      } else {
        setProducts([]);
        setError(null);
      }
      return;
    }
    const timer = setTimeout(() => fetchProducts(searchText), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const fetchProducts = async (search) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/menuRoutes/products`, {
        params: {
          root_menu_id: ROOT_MENU_ID,
          search: search || undefined,
          ...(isLive && { live_stock: 'true' }),
        },
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      setProducts(res.data);
    } catch (err) {
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchText('');
    searchInputRef.current?.focus();
  };

  return (
    <>
      {/* Root banner — always visible when set on menu id=1 */}
      {rootBannerText && (
        <div className="home-root-banner">
          <span className="home-root-banner-text">{rootBannerText}</span>
        </div>
      )}

      {/* Search + tagline hero — seamless continuation of top navbar colour */}
      <div className="home-hero">
        {/* Search bar — sticky, shrinks on scroll */}
        <div className={`search-wrapper${scrolled ? ' search-wrapper--shrunk' : ''}`}>
          <div className="home-hero-inner">
            <div className="search-bar">
              <span className="search-icon">&#128269;</span>
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder="Search products..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
              {searchText && (
                <button className="search-clear" onClick={handleClear} aria-label="Clear search">
                  &#10005;
                </button>
              )}
              {searchText && (
                <button className="search-reset" onClick={handleClear} aria-label="Reset search">
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tagline */}
        <div className="home-hero-inner">
          <p className="home-tagline">
            We are a food health-tech company with an objective to add more time to your day and more years to your life by curating clean, choiceful, cost-effective and convenient food options.
          </p>
        </div>
      </div>

    <div className="home-container">

      {/* States: loading / error / empty / grid */}
      {loading && (
        <div className="state-container">
          <div className="spinner" />
          <p>Loading products&hellip;</p>
        </div>
      )}

      {!loading && error && (
        <div className="state-container state-error">
          <p>{error}</p>
          <button className="retry-btn" onClick={() => fetchProducts(searchText)}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && products.length === 0 && (searchText || isStoreOrCS) && (
        <div className="state-container state-empty">
          <p>
            {searchText
              ? `No products found for “${searchText}”`
              : 'No products available.'}
          </p>
        </div>
      )}

      {!loading && !error && !searchText && !isStoreOrCS && (
        homeCatLoading ? (
          <div className="state-container">
            <div className="spinner" />
          </div>
        ) : (
          <div className="home-cat-grid">
            {homeCategories.map((menu) => (
              <div key={menu.id} className="home-cat-item">
                {menu.header_text && (
                  <p className="home-cat-header-text" onClick={() => navigate(menu.page_link || '/categories')} style={{ cursor: 'pointer' }}>{menu.header_text}</p>
                )}
                <button
                  className={`home-cat-tile${menu.description ? '' : ' home-cat-tile--no-desc'}`}
                  onClick={() => navigate(menu.page_link || '/categories')}
                >
                  <div className="home-cat-card-left">
                    <div className="home-cat-image-wrap">
                      {menu.image_url
                        ? <img src={menu.image_url.startsWith('http') ? menu.image_url : `${API_URL}${menu.image_url}`} alt={menu.menu_name} className="home-cat-image" />
                        : <div className="home-cat-image-placeholder">&#128230;</div>
                      }
                    </div>
                    <p className="home-cat-name">{menu.menu_name}</p>
                  </div>
                  {menu.description && (
                    <p className="home-cat-desc">{menu.description}</p>
                  )}
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {!loading && !error && products.length > 0 && (
        <div className="product-grid">
          {products.map(product => {
            const qty = getQuantity(product.id);
            const blocked = isPastCutoff(product.order_cutoff_time, product.order_after_cutoff);
            const deliveryLabel = qty > 0 ? computeDeliveryLabel(product) : null;
            return (
              <div key={product.id} className="product-tile">

                {/* Image */}
                <div className="tile-image-wrap">
                  {product.image_url ? (
                    <img
                      src={resolveImg(product.image_url)}
                      alt={product.name}
                      className="tile-image"
                    />
                  ) : (
                    <div className="tile-image-placeholder">&#128230;</div>
                  )}
                </div>

                {/* Info */}
                <div className="tile-info">
                  <h3 className="tile-name">{product.name}</h3>
                  {product.description && (
                    <DescriptionCell text={product.description} className="tile-description" />
                  )}
                  <div className="tile-price-row">
                    {formatPrice(product.price) && (
                      <span className="tile-price">{formatPrice(product.price)}</span>
                    )}
                    {formatCutoffTime(product.order_cutoff_time) && (
                      <span className="tile-cutoff">
                        Order By {formatCutoffTime(product.order_cutoff_time)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Quantity control */}
                <div className="tile-qty">
                  <button
                    className="qty-btn qty-btn--minus"
                    onClick={() => removeFromCart(product.id)}
                    disabled={qty === 0 || blocked}
                    aria-label="Remove one"
                  >
                    &#8722;
                  </button>
                  <span className="qty-value" aria-live="polite">{qty}</span>
                  <button
                    className="qty-btn qty-btn--plus"
                    onClick={() => addToCart(product)}
                    disabled={blocked || (isLive && product.current_stock != null && qty >= parseInt(product.current_stock, 10))}
                    aria-label="Add one"
                  >
                    &#43;
                  </button>
                  {deliveryLabel && (
                    <span className="tile-delivery-label">Delivery: {deliveryLabel}</span>
                  )}
                  {user?.subscription_allowed && (
                    <button
                      className="qty-btn qty-btn--subscribe"
                      onClick={() => setSubscribingProduct(product)}
                      title="Subscribe"
                      aria-label="Subscribe"
                    >
                      &#128197;
                    </button>
                  )}
                </div>

              </div>
            );
          })}
        </div>
      )}

      {subscribingProduct && (
        <SubscribeModal
          product={subscribingProduct}
          user={user}
          onClose={() => setSubscribingProduct(null)}
        />
      )}

    </div>
    </>
  );
};

export default Home;
